import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bot, Save } from 'lucide-react';
import { api } from '../api/client';
import { toast } from '../lib/toast';

// Per-domain bot-activity alert editor. Renders inside the AutoPilot card
// next to the score-drop form. Shares the same /alert-rules listing endpoint
// (filtered client-side by type).
export default function BotActivityAlertForm({ domain }) {
  const qc = useQueryClient();

  const rulesQuery = useQuery({
    queryKey: ['alert-rules', domain._id],
    queryFn: () =>
      api.get(`/api/domains/${domain._id}/autopilot/alert-rules`).then((r) => r.data.rules),
    retry: false,
  });

  const existing = (rulesQuery.data || []).find((r) => r.type === 'bot_activity');

  const [draft, setDraft] = useState(null);
  useEffect(() => {
    setDraft({
      isActive: existing?.isActive ?? false, // default off — high-traffic sites can be noisy
      emailTo: existing?.emailTo || '',
      botActivityWindowMinutes: existing?.botActivityWindowMinutes ?? 60,
      botActivityVisitsThreshold: existing?.botActivityVisitsThreshold ?? 50,
      botDormancyEnabled: existing?.botDormancyEnabled ?? false,
      botDormancyDays: existing?.botDormancyDays ?? 7,
    });
  }, [
    existing?._id,
    existing?.isActive,
    existing?.emailTo,
    existing?.botActivityWindowMinutes,
    existing?.botActivityVisitsThreshold,
    existing?.botDormancyEnabled,
    existing?.botDormancyDays,
  ]);

  const saveMutation = useMutation({
    mutationFn: (body) =>
      api
        .put(`/api/domains/${domain._id}/autopilot/alert-rules/bot-activity`, body)
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['alert-rules', domain._id] });
      toast.success('Bot-activity alert saved');
    },
    onError: (err) => toast.error(err, 'Could not save alert rule'),
  });

  if (rulesQuery.isError) return null; // gated out
  if (rulesQuery.isLoading || !draft) return null;

  const lastFiredAt = existing?.lastFiredAt
    ? new Date(existing.lastFiredAt).toLocaleString()
    : null;

  return (
    <div className="border-t border-gray-100 pt-4 mt-4 space-y-3">
      <div className="flex items-center gap-2 text-sm font-medium text-ink-800">
        <Bot className="w-4 h-4 text-cyan-600" />
        Bot-activity alert
        {draft.isActive ? (
          <span className="pill bg-emerald-50 text-emerald-700 ml-1">On</span>
        ) : (
          <span className="pill bg-gray-100 text-gray-500 ml-1">Off</span>
        )}
      </div>
      <p className="text-xs text-ink-500">
        Email you when AI crawler traffic crosses a threshold within a sliding window.
        Useful for catching a sudden GPTBot / ClaudeBot crawl wave on a quiet site. Default
        off — busy sites would get noisy.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <label className="text-sm">
          <span className="block text-xs uppercase tracking-wide text-ink-500 mb-1">
            Active
          </span>
          <button
            type="button"
            className={`w-full rounded-lg px-3 py-2 text-left text-sm transition ${
              draft.isActive
                ? 'bg-emerald-50 border border-emerald-200 text-emerald-900'
                : 'bg-gray-50 border border-gray-200 text-ink-700'
            }`}
            onClick={() => setDraft({ ...draft, isActive: !draft.isActive })}
          >
            {draft.isActive ? 'Enabled' : 'Paused'}
          </button>
        </label>

        <label className="text-sm">
          <span className="block text-xs uppercase tracking-wide text-ink-500 mb-1">
            Threshold (visits)
          </span>
          <input
            className="input"
            type="number"
            min={1}
            max={100000}
            value={draft.botActivityVisitsThreshold}
            onChange={(e) =>
              setDraft({ ...draft, botActivityVisitsThreshold: Number(e.target.value) })
            }
          />
        </label>

        <label className="text-sm">
          <span className="block text-xs uppercase tracking-wide text-ink-500 mb-1">
            Window (minutes)
          </span>
          <input
            className="input"
            type="number"
            min={5}
            max={1440}
            value={draft.botActivityWindowMinutes}
            onChange={(e) =>
              setDraft({ ...draft, botActivityWindowMinutes: Number(e.target.value) })
            }
          />
        </label>

        <label className="text-sm">
          <span className="block text-xs uppercase tracking-wide text-ink-500 mb-1">
            Email to (optional)
          </span>
          <input
            className="input"
            type="email"
            placeholder="defaults to your account email"
            value={draft.emailTo}
            onChange={(e) => setDraft({ ...draft, emailTo: e.target.value })}
          />
        </label>
      </div>

      <div className="border-t border-gray-100 pt-3 mt-1 space-y-2">
        <div className="flex items-center gap-2 text-sm font-medium text-ink-800">
          Bot “went quiet” alert
          {draft.botDormancyEnabled ? (
            <span className="pill bg-emerald-50 text-emerald-700 ml-1">On</span>
          ) : (
            <span className="pill bg-gray-100 text-gray-500 ml-1">Off</span>
          )}
        </div>
        <p className="text-xs text-ink-500">
          Email you when a bot that <em>used to</em> crawl {domain.domain} goes silent — an
          early warning that you may have dropped out of an AI engine's answer pool.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="text-sm">
            <span className="block text-xs uppercase tracking-wide text-ink-500 mb-1">
              Dormancy alert
            </span>
            <button
              type="button"
              className={`w-full rounded-lg px-3 py-2 text-left text-sm transition ${
                draft.botDormancyEnabled
                  ? 'bg-emerald-50 border border-emerald-200 text-emerald-900'
                  : 'bg-gray-50 border border-gray-200 text-ink-700'
              }`}
              onClick={() =>
                setDraft({ ...draft, botDormancyEnabled: !draft.botDormancyEnabled })
              }
            >
              {draft.botDormancyEnabled ? 'Enabled' : 'Paused'}
            </button>
          </label>
          <label className="text-sm">
            <span className="block text-xs uppercase tracking-wide text-ink-500 mb-1">
              Quiet after (days)
            </span>
            <input
              className="input"
              type="number"
              min={1}
              max={90}
              value={draft.botDormancyDays}
              onChange={(e) =>
                setDraft({ ...draft, botDormancyDays: Number(e.target.value) })
              }
            />
          </label>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        {lastFiredAt && (
          <div className="text-xs text-ink-400">Last fired: {lastFiredAt}</div>
        )}
        <button
          type="button"
          className="btn-primary text-sm ml-auto"
          disabled={saveMutation.isPending}
          onClick={() => saveMutation.mutate(draft)}
        >
          <Save className="w-4 h-4" />
          {saveMutation.isPending ? 'Saving…' : 'Save alert rule'}
        </button>
      </div>
    </div>
  );
}
